<script setup lang="ts">
import { AppLinkButton, AppRawButton, AppStars, AppSvgIcon } from '@libs/ui';

import type { ActivityProductCardContent } from '../../../models';

interface ActivityAlternativeProductCardContentProps {
  productCardContent: ActivityProductCardContent;
}

const props = defineProps<ActivityAlternativeProductCardContentProps>();

interface ActivityAlternativeProductCardContentEmits {
  (eventName: 'show-details'): void;
  (eventName: 'show-tickets'): void;
  (event: 'mouse-enter-content'): void;
  (event: 'mouse-leave-content'): void;
}

const emit = defineEmits<ActivityAlternativeProductCardContentEmits>();
</script>

<template>
  <AppRawButton
    class="activity-alternative-product-card-content"
    @click="emit('show-details')"
  >
    <div class="details">
      <div class="upper-section">
        <AppRawButton class="name" @click="emit('show-details')">
          {{ props.productCardContent.header }}
        </AppRawButton>
        <div class="location">{{ props.productCardContent.subheader }}</div>
        <AppStars :amount="props.productCardContent.rating.stars" size="18" />
      </div>

      <div class="middle-section">
        <AppRawButton
          class="alternative-tickets"
          @click.stop
          @mouseenter="emit('mouse-leave-content')"
          @focus="emit('mouse-leave-content')"
          @mouseleave="emit('mouse-enter-content')"
          @blur="emit('mouse-enter-content')"
        >
          <div class="alternative-tickets-reason">
            <AppSvgIcon name="infoCircle" />
            {{ $t('planner.no_availability.messages.alternative_tickets') }}
          </div>

          <div class="alternative-tickets-info">
            <div
              v-for="element in props.productCardContent.cardDetails"
              :key="element.label + element.description"
              class="row"
            >
              <div class="label">{{ element.label }}:</div>
              <div class="description">
                {{ element.description }}
              </div>
            </div>
          </div>

          <div class="alternative-tickets-actions">
            <AppLinkButton
              :text="$t('planner.edit_tickets')"
              size="s"
              @click="emit('show-tickets')"
            />
          </div>
        </AppRawButton>
      </div>
    </div>
  </AppRawButton>
</template>

<style scoped lang="scss">
.activity-alternative-product-card-content {
  height: 100%;

  @include for-less-or-equal-than-599-px {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
  }

  .details {
    width: 100%;
    height: 100%;
    padding: var(--spacing-16) var(--spacing-12);

    display: flex;
    flex-flow: column;
    flex: 1;

    .upper-section {
      padding-bottom: var(--spacing-16);

      .name {
        padding: 0;
        @include font-label-l-bold;
        margin-bottom: var(--spacing-4);

        @include on-hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }

      .location {
        @include font-label-s-light;
        margin-bottom: var(--spacing-4);
      }
    }

    .middle-section {
      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .label {
          @include font-label-s-bold;
          white-space: nowrap;
        }
        .description {
          @include font-label-s-light;
        }
      }

      .alternative-tickets {
        display: flex;
        flex-direction: column;
        align-items: start;
        background: var(--color-brand-summer);
        padding: var(--spacing-16);
        border-radius: var(--border-radius-8);
        cursor: default;

        .alternative-tickets-reason {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-align: left;
          gap: var(--spacing-12);
          margin-bottom: var(--spacing-8);
          @include font-label-s-light;
        }

        .alternative-tickets-info {
          border-top: 1px solid var(--color-overlay);
          .row {
            display: flex;
            gap: var(--spacing-4);
            margin-top: var(--spacing-8);

            .label {
              @include font-label-s-bold;
              white-space: nowrap;
            }
            .description {
              @include font-label-s-light;
            }
          }
        }

        .alternative-tickets-actions {
          display: flex;
          gap: var(--spacing-8);
          margin-top: var(--spacing-16);
          @include font-label-s-light;
        }
      }
    }
  }

  .price {
    align-self: center;
  }
}
</style>
